import { useTranslations } from "next-intl";
import { useState } from "react";

import { Avatar, Typeahead, TypeaheadItem } from "@zapier/design-system";

import { Member, useMembers } from "./useMembers";

type Props = {
  accountId?: string;
  /** True to allow selection of multiple items */
  allowMultipleSelections?: boolean;
  ariaLabel: string;
  /** Pass to render selected items outside of dropdown */
  children?: (props: {
    memberIds: string[];
    selectedMembers: Member[];
  }) => React.ReactElement[];
  menuWidth?: string;
  id?: string;
  menuAriaLabel: string;
  menuAlign?: "right" | "left" | "stretch";
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  userId: string | undefined;
  value: string[] | undefined;
};

type MemberItem = Member & { name: string; __isLoadMore?: boolean };

function toItem(member: Member): MemberItem {
  return { ...member, name: member.name || member.email || "" };
}

const LOADING_RECORD_ID = -1;
const LOAD_MORE_RECORD_ID = -2;

export function UserFilter(props: Props) {
  const t = useTranslations("user-filter");
  const tCommon = useTranslations("common");
  const memberIds = props.value || [];
  const [inputValue, setInputValue] = useState("");
  const { onChange, accountId, userId } = props;
  const {
    isLoading,
    isLoadingMore,
    members,
    onLoadMore,
    onSearch,
    selectedMembers,
  } = useMembers({ accountId, memberIds, userId });

  const showLoadMore = Boolean(onLoadMore) && !isLoadingMore;

  const allItems: MemberItem[] = [
    ...members.map(toItem),
    ...(isLoadingMore
      ? [
          {
            recordId: LOADING_RECORD_ID,
            name: tCommon("loading"),
            email: "",
            __isLoadMore: true,
          } as MemberItem,
        ]
      : showLoadMore
        ? [
            {
              recordId: LOAD_MORE_RECORD_ID,
              name: tCommon("load-more"),
              email: "",
              __isLoadMore: true,
            } as MemberItem,
          ]
        : []),
  ];

  return (
    <>
      <Typeahead
        aria-label={props.ariaLabel}
        iconLeft="personOne"
        isBusy={isLoading}
        id={props.id}
        inputValue={inputValue}
        items={allItems}
        menuTrigger="focus"
        menuWidth={props.menuWidth}
        onInputChange={(v) => {
          setInputValue(v);
          onSearch(v);
        }}
        onSelectionChange={(key) => {
          const numKey = Number(key);
          if (numKey === LOADING_RECORD_ID) return;
          if (numKey === LOAD_MORE_RECORD_ID) {
            onLoadMore?.();
            return;
          }
          onChange(key ? numKey : undefined);
          setInputValue("");
        }}
        placeholder={props.placeholder || t("default-placeholder")}
        selectedKey={null}
        size="medium"
      >
        {(dsItem) => {
          const item = dsItem as MemberItem;
          return (
            <TypeaheadItem
              id={String(item.recordId)}
              isDisabled={item.recordId === LOADING_RECORD_ID}
              textValue={item.name}
            >
              {item.__isLoadMore ? (
                item.name
              ) : (
                <>
                  <Avatar
                    aria-hidden={true}
                    name={item.name}
                    size="small"
                    url={item.imageUrl}
                  />
                  {item.name}
                </>
              )}
            </TypeaheadItem>
          );
        }}
      </Typeahead>
      {props.children && props.children({ memberIds, selectedMembers })}
    </>
  );
}
